import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { getSession } from '../utils/auth';
import { useAuth } from '../context/AuthContext';
import { useMarket } from '../context/MarketContext';
import { resolveMarketPath } from '../hooks/useMarketNavigate';

/**
 * Protected Route component that checks authentication and role.
 *
 * Supports two auth modes:
 *   - Legacy (TCC): Shared access codes via utils/auth.js
 *   - New RBAC (OTC): Email/password via AuthContext
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {string} props.requiredRole - Required role for legacy auth ('subscriber' or 'admin')
 * @param {string[]} props.allowedRoles - Allowed roles for new auth (['super_admin', 'admin', 'venue_owner'])
 */
function ProtectedRoute({ children, requiredRole, allowedRoles }) {
  const market = useMarket();
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const { marketSlug } = useParams();

  const useNewAuth = market.features?.venueOwnerAccounts === true;

  if (useNewAuth) {
    // --- New RBAC auth ---

    // Show nothing while validating token on initial load
    if (isLoading) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          color: '#666',
          fontSize: '1rem',
        }}>
          Loading...
        </div>
      );
    }

    // Not authenticated — redirect to login with return URL
    if (!isAuthenticated) {
      return <Navigate to={resolveMarketPath('/login', marketSlug)} state={{ from: location }} replace />;
    }

    // Check if password must be changed
    if (user?.passwordMustChange && location.pathname !== resolveMarketPath('/change-password', marketSlug)) {
      return <Navigate to={resolveMarketPath('/change-password', marketSlug)} replace />;
    }

    // Check role-based access
    if (allowedRoles && allowedRoles.length > 0) {
      // super_admin always has access
      if (user.role !== 'super_admin' && !allowedRoles.includes(user.role)) {
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            color: '#666',
            textAlign: 'center',
            padding: '2rem',
          }}>
            <h2 style={{ color: '#333', marginBottom: '0.5rem' }}>Not Authorized</h2>
            <p>You don't have permission to access this page.</p>
            <a href={resolveMarketPath('/', marketSlug)} style={{ color: market.theme.primaryColor, marginTop: '1rem' }}>Go to Home</a>
          </div>
        );
      }
    }

    // Authorized — render children
    return children;

  } else {
    // --- Legacy shared-code auth (TCC) ---
    const session = getSession();

    if (!session) {
      return <Navigate to="/login" replace />;
    }

    if (requiredRole && session.role !== requiredRole) {
      if (requiredRole === 'admin' && session.role === 'subscriber') {
        return <Navigate to="/" replace />;
      }
      return <Navigate to="/login" replace />;
    }

    return children;
  }
}

export default ProtectedRoute;
